import { browserJoinVisitId, applePassUrl } from "./join-visit";
import demo from "@collectie/test-fixtures/demo";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrandMark } from "./BrandMark";
import { t, type Locale } from "./i18n";

// Keep the executable demo contract linked to this route for blueprint drift checks.
void demo;

export interface PublicLocation {
  slug: string;
  displayName: string;
  timezone: string;
  requestedLocale: Locale;
  fallbackFields: Array<"merchant" | "location" | "program">;
  merchant: {
    slug: string;
    displayName: string;
    shortDescription: string | null;
    logoUrl: string | null;
    brandColor: string;
    websiteUrl: string | null;
  };
  program: {
    version: number;
    type: "visit_stamp" | "item_stamp" | "spend_points";
    threshold: number;
    earnQuantity: number;
    name: string;
    description: string | null;
    rewardLabel: string;
    interimRewards?: Array<{ stamps: number; label: string }>;
    terms: string | null;
  };
}

export type JoinState =
  | { status: "loading" }
  | { status: "ready"; location: PublicLocation }
  | { status: "not-found" }
  | { status: "error" };

type ContinueState = "idle" | "creating" | "ready" | "error";
type SessionState = "idle" | "creating" | "ready" | "error";
type Copy = (key: string, values?: Record<string, string | number>) => string;
interface Requester {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

function chooseLocale(): Locale {
  if (typeof navigator === "undefined") return "en";
  const preferred = navigator.languages.join(",").toLowerCase();
  if (preferred.includes("de")) return "de";
  return preferred.includes("zh-hant") ||
    preferred.includes("zh-tw") ||
    preferred.includes("zh-hk")
    ? "zh-Hant"
    : "en";
}

export function applyDocumentLocale(
  locale: Locale,
  root: { lang: string } = document.documentElement,
): void {
  root.lang = locale;
}

// The Apple pass links here, so this page must stand on its own: it says what
// the app adds, states plainly when it cannot be installed yet, and never
// suggests the Wallet card stops working.
export function AppLandingPage({
  copy,
  appStoreUrl,
}: {
  copy: Copy;
  appStoreUrl: string | null;
}) {
  return (
    <section className="join-content app-landing">
      <div className="join-intro">
        <h1>{copy("app.title")}</h1>
        <p className="join-lede">{copy("app.lede")}</p>
      </div>
      {appStoreUrl ? (
        <a className="primary-action" href={appStoreUrl}>
          {copy("app.download")}
        </a>
      ) : (
        <div className="join-status" role="status">
          <h2>{copy("app.comingSoonTitle")}</h2>
          <p>{copy("app.comingSoonBody")}</p>
        </div>
      )}
      <div className="fallback-note">
        <strong>{copy("app.cardKeeps")}</strong>
        <p>{copy("app.cardKeepsBody")}</p>
      </div>
    </section>
  );
}

function isAppRoute(): boolean {
  if (typeof window === "undefined") return false;
  return /^\/app\/?$/.test(window.location.pathname);
}

function configuredAppStoreUrl(): string | null {
  if (typeof document === "undefined") return null;
  const value = document
    .querySelector('meta[name="collectie-app-store-url"]')
    ?.getAttribute("content");
  return value && value.startsWith("https://") ? value : null;
}

function routeSlug(): string | null {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(
    /^\/join\/([a-z0-9]+(?:-[a-z0-9]+)*)$/,
  );
  return match?.[1] ?? null;
}

function isPublicLocation(value: unknown): value is PublicLocation {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const location = value as Record<string, unknown>;
  const merchant =
    location.merchant &&
    typeof location.merchant === "object" &&
    !Array.isArray(location.merchant)
      ? (location.merchant as Record<string, unknown>)
      : null;
  const program =
    location.program &&
    typeof location.program === "object" &&
    !Array.isArray(location.program)
      ? (location.program as Record<string, unknown>)
      : null;
  return (
    typeof location.slug === "string" &&
    typeof location.displayName === "string" &&
    typeof location.timezone === "string" &&
    (location.requestedLocale === "en" ||
      location.requestedLocale === "zh-Hant") &&
    Array.isArray(location.fallbackFields) &&
    location.fallbackFields.every((field) =>
      ["merchant", "location", "program"].includes(String(field)),
    ) &&
    merchant !== null &&
    typeof merchant.slug === "string" &&
    typeof merchant.displayName === "string" &&
    (merchant.shortDescription === null ||
      typeof merchant.shortDescription === "string") &&
    (merchant.logoUrl === null || typeof merchant.logoUrl === "string") &&
    typeof merchant.brandColor === "string" &&
    (merchant.websiteUrl === null || typeof merchant.websiteUrl === "string") &&
    program !== null &&
    typeof program.version === "number" &&
    ["visit_stamp", "item_stamp", "spend_points"].includes(
      String(program.type),
    ) &&
    typeof program.threshold === "number" &&
    typeof program.earnQuantity === "number" &&
    typeof program.name === "string" &&
    (program.description === null || typeof program.description === "string") &&
    typeof program.rewardLabel === "string" &&
    (program.terms === null || typeof program.terms === "string")
  );
}

function initials(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0] ?? "")
    .join("")
    .toUpperCase();
}

function ShopMark({
  merchant,
  className = "",
}: {
  merchant: PublicLocation["merchant"];
  className?: string;
}) {
  return (
    <span className={`shop-monogram ${className}`.trim()} aria-hidden="true">
      {merchant.logoUrl ? (
        <img src={merchant.logoUrl} alt="" />
      ) : (
        initials(merchant.displayName)
      )}
    </span>
  );
}

export async function establishAnonymousSession(
  locationSlug: string,
  locale: Locale,
  requester: Requester = fetch,
): Promise<void> {
  const response = await requester("/v1/consumer/anonymous-sessions", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "accept-language": locale,
      "content-type": "application/json",
    },
    body: JSON.stringify({
      locationSlug,
      joinVisitId: browserJoinVisitId(locationSlug),
    }),
  });
  if (!response.ok) throw new Error("ANONYMOUS_SESSION_CREATE_FAILED");
  const body: unknown = await response.json();
  if (
    !body ||
    typeof body !== "object" ||
    Array.isArray(body) ||
    (body as Record<string, unknown>).personState !== "provisional" ||
    !(body as Record<string, unknown>).membership ||
    typeof (body as { membership?: { id?: unknown } }).membership?.id !==
      "string"
  ) {
    throw new Error("ANONYMOUS_SESSION_RESPONSE_INVALID");
  }
}

export const APPLE_PASS_PATH = "/v1/consumer/wallet/apple/pass";

// A top-level GET navigation is the only handoff iOS Safari accepts: it shows
// Wallet's Add Pass sheet for the .pkpass response. A POST fails with "Safari
// cannot download this file" because Safari cannot re-issue the request.
function requestApplePassDownload(locationSlug: string): void {
  window.location.assign(applePassUrl(locationSlug));
}

function Header({
  copy,
  locale,
  onLocale,
}: {
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
}) {
  return (
    <header className="join-header">
      <a className="join-brand" href="/" aria-label="Collectie">
        <BrandMark />
        Collectie
      </a>
      <select
        className="locale-button"
        value={locale}
        aria-label={copy("language.choose")}
        onChange={(event) => onLocale(event.target.value as Locale)}
      >
        <option value="en">{copy("language.english")}</option>
        <option value="zh-Hant">{copy("language.traditionalChinese")}</option>
        <option value="de">{copy("language.german")}</option>
      </select>
    </header>
  );
}

function Loading({ copy }: { copy: Copy }) {
  return (
    <section className="join-status" aria-busy="true" aria-live="polite">
      <div className="join-loader" aria-hidden="true" />
      <p>{copy("join.loading")}</p>
    </section>
  );
}

function Failure({
  copy,
  notFound,
  onRetry,
}: {
  copy: Copy;
  notFound: boolean;
  onRetry: () => void;
}) {
  return (
    <section className="join-status" role="alert">
      <span className="status-mark" aria-hidden="true">
        {notFound ? "?" : "!"}
      </span>
      <h1>{copy(notFound ? "join.notFoundTitle" : "join.loadErrorTitle")}</h1>
      <p>{copy(notFound ? "join.notFoundBody" : "join.loadErrorBody")}</p>
      {!notFound && (
        <button className="primary-action" type="button" onClick={onRetry}>
          {copy("common.retry")}
        </button>
      )}
    </section>
  );
}

function JoinPage({
  location,
  copy,
  continueState,
  onContinue,
}: {
  location: PublicLocation;
  copy: Copy;
  continueState: ContinueState;
  onContinue: (locationSlug: string) => void;
}) {
  const { merchant, program } = location;
  const brandedStyle = {
    "--shop-accent": merchant.brandColor,
  } as CSSProperties;
  return (
    <section className="join-content join-page" style={brandedStyle}>
      <section className="join-hero">
        <div className="join-intro">
          <div className="merchant-context">
            <ShopMark merchant={merchant} />
            <span>
              <strong>{merchant.displayName}</strong>
              <small>{location.displayName}</small>
            </span>
          </div>
          <p className="eyebrow">{copy("join.eyebrow")}</p>
          <h1>{copy("join.title", { merchant: merchant.displayName })}</h1>
          <p className="join-lede">
            {merchant.shortDescription ?? copy("join.body")}
          </p>
          {merchant.websiteUrl && (
            <a
              className="merchant-website"
              href={merchant.websiteUrl}
              rel="noreferrer"
            >
              {copy("join.merchantWebsite")}
            </a>
          )}

          <div className="join-primary-action">
            <button
              id="add-to-wallet"
              className="wallet-action"
              type="button"
              disabled={
                continueState === "creating" || continueState === "ready"
              }
              onClick={() => onContinue(location.slug)}
            >
              <span className="apple-mark" aria-hidden="true">
                
              </span>
              <span>
                {copy(
                  continueState === "creating"
                    ? "join.creating"
                    : continueState === "ready"
                      ? "join.added"
                      : "join.addWallet",
                )}
              </span>
            </button>
            {continueState === "error" && (
              <p className="action-error" role="alert">
                {copy("join.actionError")}
              </p>
            )}
            <div className="join-assurance">
              <strong>{copy("join.noApp")}</strong>
              <span>{copy("join.noPii")}</span>
            </div>
          </div>
        </div>

        <aside className="wallet-preview" aria-hidden="true">
          <div className="wallet-preview-card">
            <div className="wallet-preview-brand">
              <BrandMark />
              <strong>Collectie</strong>
            </div>
            <div className="wallet-preview-message">
              <span>{copy("join.previewLabel")}</span>
              <strong>{copy("join.previewTitle")}</strong>
            </div>
            <div className="wallet-preview-shop">
              <ShopMark merchant={merchant} className="wallet-shop-logo" />
              <span>{copy("join.atThisShop")}</span>
              <strong>{merchant.displayName}</strong>
            </div>
            <span className="wallet-preview-orbit" />
          </div>
        </aside>
      </section>

      {location.fallbackFields.length > 0 && (
        <p className="fallback-note" role="status">
          {copy("join.fallback")}
        </p>
      )}

      <article className="public-program">
        <div className="program-heading">
          <ShopMark merchant={merchant} />
          <div>
            <p>{copy("join.programDetails")}</p>
            <h2>{program.name}</h2>
          </div>
        </div>
        {program.description && (
          <p className="program-description">{program.description}</p>
        )}
        <div className="program-facts">
          <div>
            <strong>{program.threshold}</strong>
            <span>
              {copy(
                program.interimRewards?.length
                  ? "merchant.program.finalStamps"
                  : "join.stampsToReward",
              )}
            </span>
          </div>
          <div>
            <strong>+{program.earnQuantity}</strong>
            <span>{copy("join.perPurchase")}</span>
          </div>
        </div>
        <div className="reward-row">
          <span>{copy("join.reward")}</span>
          {program.interimRewards?.map((reward) => (
            <p key={reward.stamps}>
              {copy("merchant.program.milestoneSummary", {
                stamps: new Intl.NumberFormat(location.requestedLocale).format(
                  reward.stamps,
                ),
                reward: reward.label,
              })}
            </p>
          ))}
          <strong>{program.rewardLabel}</strong>
        </div>
        {program.terms && (
          <details className="terms">
            <summary>{copy("join.terms")}</summary>
            <p>{program.terms}</p>
          </details>
        )}
      </article>
      <div className="join-footnotes">
        <p className="join-safety">{copy("join.safeJoin")}</p>
        <p className="privacy">{copy("join.privacy")}</p>
      </div>
    </section>
  );
}

export function App({
  initialLocale,
  initialState,
  initialSlug,
  initialRoute,
  appStoreUrl,
  onContinue,
}: {
  initialLocale?: Locale;
  initialState?: JoinState;
  initialSlug?: string;
  initialRoute?: "join" | "app";
  appStoreUrl?: string | null;
  onContinue?: (locationSlug: string) => void;
}) {
  const [locale, setLocale] = useState<Locale>(initialLocale ?? chooseLocale);
  const [state, setState] = useState<JoinState>(
    initialState ?? { status: "loading" },
  );
  const [retry, setRetry] = useState(0);
  const route = initialRoute ?? (isAppRoute() ? "app" : "join");
  const storeUrl = appStoreUrl ?? configuredAppStoreUrl();
  const [continueState, setContinueState] = useState<ContinueState>("idle");
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const preparedSlug = useRef<string | null>(null);
  const slug = initialSlug ?? routeSlug();
  const copy = useMemo<Copy>(
    () =>
      (key, values = {}) =>
        t(locale, key, values),
    [locale],
  );

  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (initialState || route === "app") return;
    if (!slug) {
      setState({ status: "not-found" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    void fetch(`/v1/public/locations/${encodeURIComponent(slug)}`, {
      headers: { "accept-language": locale },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 404) {
          setState({ status: "not-found" });
          return;
        }
        if (!response.ok) {
          setState({ status: "error" });
          return;
        }
        const value: unknown = await response.json();
        setState(
          isPublicLocation(value)
            ? { status: "ready", location: value }
            : { status: "error" },
        );
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setState({ status: "error" });
        }
      });
    return () => controller.abort();
  }, [initialState, locale, retry, route, slug]);

  // The documented join sequence provisions the anonymous session while the
  // page loads, before the customer taps. Keeping it here means the tap itself
  // only submits the pass request, so the navigation stays inside the tap's
  // user activation window — Safari hands a .pkpass to Wallet only for a
  // navigation it still considers user-initiated.
  useEffect(() => {
    if (initialState || onContinue) return;
    if (state.status !== "ready") return;
    const locationSlug = state.location.slug;
    if (preparedSlug.current === locationSlug) return;
    preparedSlug.current = locationSlug;
    setSessionState("creating");
    establishAnonymousSession(locationSlug, locale).then(
      () => setSessionState("ready"),
      () => setSessionState("error"),
    );
  }, [initialState, locale, onContinue, state]);

  const continueToWallet = (locationSlug: string) => {
    if (continueState === "creating" || continueState === "ready") return;
    if (!onContinue && sessionState === "ready") {
      requestApplePassDownload(locationSlug);
      setContinueState("ready");
      return;
    }
    setContinueState("creating");
    void (async () => {
      try {
        if (onContinue) {
          await onContinue(locationSlug);
        } else {
          await establishAnonymousSession(locationSlug, locale);
          setSessionState("ready");
          requestApplePassDownload(locationSlug);
        }
        setContinueState("ready");
      } catch {
        setContinueState("error");
      }
    })();
  };

  if (route === "app") {
    return (
      <main className="join-shell">
        <Header copy={copy} locale={locale} onLocale={setLocale} />
        <AppLandingPage copy={copy} appStoreUrl={storeUrl} />
        <footer>{copy("brand.tagline")}</footer>
      </main>
    );
  }

  return (
    <main className="join-shell">
      <Header copy={copy} locale={locale} onLocale={setLocale} />
      {state.status === "loading" && <Loading copy={copy} />}
      {state.status === "not-found" && (
        <Failure
          copy={copy}
          notFound
          onRetry={() => setRetry((current) => current + 1)}
        />
      )}
      {state.status === "error" && (
        <Failure
          copy={copy}
          notFound={false}
          onRetry={() => setRetry((current) => current + 1)}
        />
      )}
      {state.status === "ready" && (
        <JoinPage
          location={state.location}
          copy={copy}
          continueState={continueState}
          onContinue={continueToWallet}
        />
      )}
      <footer>{copy("brand.tagline")}</footer>
    </main>
  );
}
